import { isEmpty } from 'lodash-es';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { ActiveSortTableColumnProvider } from 'src/contexts/ActiveSortTableColumnContext';
import { CatalogMultiSelectionContextProvider } from 'src/contexts/CatalogMultiSelectionContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useMultiSelectionContextItemSelectionSelector } from 'src/contexts/MultiSelectionContext';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { shared, vars } from 'src/core/themes';
import { Stack, Switch } from 'src/core/ui';
import { useGetListingMergeSuggestionsByEvent } from 'src/hooks/api/useGetListingMergeSuggestionsByEvent';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { FieldError } from 'src/modals/common/Purchase';
import { ListingTable } from 'src/tables/ListingTable';
import { ContentId } from 'src/utils/constants/contentId';
import { DefaultListingTableSorting } from 'src/utils/constants/tableColumns';
import {
  ActionOutboxEntityType,
  Event,
  EventWithData,
  ListingGroup,
} from 'src/WebApiController';
import { Feature, Listing, MergeListingsInput } from 'src/WebApiController';

import {
  MultiSelectionContextProvider,
  MultiSelectScope,
  useMultiSelectionContext,
} from '../../contexts/MultiSelectionContext/MultiSelectionContext';
import { isEqualIgnoringOrder } from './BulkMergeListings/BulkMergeListings.utils';
import { useMergeExclusivityWarnings } from './BulkMergeListings/useMergeExclusivityWarnings';
import * as styles from './MergeListings.css';
import { MainLabel } from './MergeListings.styled';
import { MergeListingsSuggestionsDisplay } from './MergeListingsSuggestionsDisplay';
import { getFlattenedListings } from './utils';

export type MergeListingsBodyProps = EventWithData & {
  disabled?: boolean;
  onMergeSuggestions: (listingIds: number[]) => void;
  footer?: React.ReactElement;
  // POS-7295: the whole section-type disambiguation UI is gated behind this feature flag.
  hasGaMergedMaxDisplayFeature?: boolean;
  // POS-7295: the picker. Rendered above the suggestions for a table merge, otherwise directly under the
  // suggestion it belongs to.
  sectionPromptNode?: React.ReactNode;
  sectionPromptAboveSuggestions?: boolean;
  // POS-7295: the suggestion whose section pick is pending, and whether a section has been picked yet.
  pendingSectionListingIds?: number[];
  sectionTypeSelected?: boolean;
};

export const MergeListingsBody = ({
  event,
  entities,
  footer,
  disabled,
  onMergeSuggestions,
  hasGaMergedMaxDisplayFeature,
  sectionPromptNode,
  sectionPromptAboveSuggestions,
  pendingSectionListingIds,
  sectionTypeSelected,
  ...rest
}: MergeListingsBodyProps) => {
  const listings = entities.listings;

  const { watch, formState, setValue } = useFormContext<MergeListingsInput>();
  const hasDefaultNotToMergeHoldListingsFeature = useUserHasFeature(
    Feature.DefaultNotToMergeHoldListings
  );

  const [excludeHeldListings, setExcludeHeldListings] = useState<boolean>(
    hasDefaultNotToMergeHoldListingsFeature
  );

  const hasAdminHoldListings = useMemo(
    () => !isEmpty(listings?.filter((l) => l.isAdminHold)),
    [listings]
  );

  const eligibleListings = useMemo(
    (): (Listing | ListingGroup)[] =>
      (listings?.filter((l) => {
        let eligible = !l.isSeatSaver;
        if (excludeHeldListings) {
          eligible = eligible && !l.isAdminHold;
        }

        return eligible;
      }) || []) as (Listing | ListingGroup)[],
    [excludeHeldListings, listings]
  );

  const listingIds = watch('listingIds');

  // POS-7295: clicking "Merge Suggested" merges a suggestion, not the table selection, so clear the
  // table selection to keep the merge target unambiguous. `clearSelection` lives inside the
  // MultiSelectionContextProvider rendered below, hence the bridge/ref indirection.
  const clearSelectionRef = useRef<() => void>(() => undefined);

  const handleMergeSuggestions = useCallback(
    (suggestionListingIds: number[]) => {
      if (hasGaMergedMaxDisplayFeature) {
        clearSelectionRef.current();
        setValue('listingIds', []);
      }
      onMergeSuggestions(suggestionListingIds);
    },
    [hasGaMergedMaxDisplayFeature, onMergeSuggestions, setValue]
  );

  const [ineligibilityCount, setIneligibilityCount] = useState(0);

  const { mergeSuggestions } = useGetListingMergeSuggestionsByEvent(
    event,
    excludeHeldListings
  );

  const hasEnableOfficeFeature = useUserHasFeature(Feature.EnableOffice);

  const sectionDifMsg = useContent(ContentId.MergeListings_SectionDifference);
  const rowDifMsg = useContent(ContentId.MergeListings_RowDifference);
  const deliveryDifMsg = useContent(ContentId.MergeListings_DeliveryDifference);
  const groupedListingMsg = useContent(ContentId.MergeListings_GroupedListings);
  const mismatchListingMsg = useContent(
    ContentId.MergeListings_ListingMismatchWarning
  );
  const officeMismatchMsg = useContent(
    ContentId.MergeListings_StorefrontMismatchWarning
  );
  const duplicateListingMsg = useContent(
    ContentId.MergeListings_DuplicateListingWarning
  );

  const getListingIneiligibleReason = useCallback(
    (firstListing: Listing, l: Listing) => {
      return l.seating.section !== firstListing.seating.section
        ? sectionDifMsg
        : (l.seating.row || null) !== (firstListing.seating.row || null)
          ? rowDifMsg
          : l.delivType !== firstListing.delivType
            ? deliveryDifMsg
            : l.isLtGrp
              ? groupedListingMsg
              : undefined;
    },
    [deliveryDifMsg, groupedListingMsg, rowDifMsg, sectionDifMsg]
  );

  const getListingNotEligibleForSelection = useCallback(
    (listings?: Listing[] | null) => {
      const selectedListings = (listings ?? []).filter((l) =>
        listingIds?.includes(l.id)
      );

      if (selectedListings?.length) {
        const listingsWithIneligibleReasons = (listings ?? [])!.map((l) => ({
          listing: l,
          ineligibleReason: undefined,
        }));

        const newIneligibilityCount = listingsWithIneligibleReasons.filter(
          (l) => l.ineligibleReason
        ).length;

        if (newIneligibilityCount !== ineligibilityCount) {
          setIneligibilityCount(newIneligibilityCount);
        }
        return listingsWithIneligibleReasons;
      }
    },
    [ineligibilityCount, listingIds]
  );

  const mergeMismatchListingWarning = useMemo(() => {
    const selectedListings = (eligibleListings ?? []).filter((l) =>
      listingIds?.includes(l.id)
    );

    if (selectedListings.length) {
      const firstListing = selectedListings[0];
      const hasMismatch = selectedListings.some((l, index) => {
        return (
          index > 0 && getListingIneiligibleReason(firstListing, l) != null
        );
      });

      if (hasMismatch) {
        return mismatchListingMsg;
      }
    }

    return undefined;
  }, [
    getListingIneiligibleReason,

    listingIds,
    mismatchListingMsg,
    eligibleListings,
  ]);

  const mergeOfficeMismatchWarning = useMemo(() => {
    if (!hasEnableOfficeFeature) return undefined;
    const selectedListings = (eligibleListings ?? []).filter((l) =>
      listingIds?.includes(l.id)
    );
    if (selectedListings.length < 2) return undefined;
    const officeIds = new Set(selectedListings.map((l) => l.officeId));
    return officeIds.size > 1 ? officeMismatchMsg : undefined;
  }, [hasEnableOfficeFeature, eligibleListings, listingIds, officeMismatchMsg]);

  // POS-7568: warn when a selected listing is a flagged duplicate that the seller has not
  // allowed to broadcast (IgnoreDuplicate off).
  const mergeDuplicateListingWarning = useMemo(() => {
    const selectedListings = (eligibleListings ?? []).filter((l) =>
      listingIds?.includes(l.id)
    );
    const hasDuplicate = selectedListings.some(
      (l) => l.dupListingId != null && !l.ignoreDup
    );
    return hasDuplicate ? duplicateListingMsg : undefined;
  }, [eligibleListings, listingIds, duplicateListingMsg]);

  const mergeSuggestionsListingIds = useMemo(() => {
    if (!mergeSuggestions) {
      return [];
    }

    return mergeSuggestions.map((s) => s.listingIds);
  }, [mergeSuggestions]);

  // POS-9467: one classification call for every suggested group, so each suggestion can warn when it
  // mixes exclusivity classes.
  const exclusivitySourceClassesBySuggestion = useMergeExclusivityWarnings(
    mergeSuggestionsListingIds
  );

  const [flattenedEligibleListings, groupIdNameMap] =
    getFlattenedListings(eligibleListings);

  return (
    <Stack direction="column" gap="l">
      <MainLabel>
        <Content id={ContentId.SelectListingsToMerge} />
        {hasAdminHoldListings && hasDefaultNotToMergeHoldListingsFeature && (
          <Stack
            direction="row"
            gap="s"
            alignItems="center"
            style={{ fontSize: vars.typography.fontSize.base, fontWeight: 500 }}
          >
            <Switch
              checked={!excludeHeldListings}
              onChange={(e) => e.stopPropagation()}
              onCheckedChange={(isChecked) => {
                if (isChecked === excludeHeldListings) {
                  setExcludeHeldListings(!isChecked);
                }
              }}
            />
            <Content id={ContentId.IncludeHeldListings} />
          </Stack>
        )}
      </MainLabel>
      <CatalogMultiSelectionContextProvider type="listing">
        <MultiSelectionContextProvider
          isServerSidePagination={false}
          initialSelectionMode={{
            mode: MultiSelectScope.SingleGroup,
            groupId: event.virtId,
          }}
        >
          <ClearSelectionBridge clearSelectionRef={clearSelectionRef} />
          <Stack direction="column" gap="m" className={styles.tableContainer}>
            <ActiveSortTableColumnProvider
              entityType={ActionOutboxEntityType.Listing}
              defaultSorting={DefaultListingTableSorting}
            >
              {/* Rendered inside MultiSelectionContextProvider so it can read
                  the row selection from context and mirror it into the form —
                  see the component's doc comment (POS-8673). */}
              <MergeListingsSelectionTable
                event={event}
                eligibleListings={eligibleListings}
                listingIds={listingIds}
                getListingNotEligibleForSelection={
                  getListingNotEligibleForSelection
                }
              />
            </ActiveSortTableColumnProvider>

            {mergeMismatchListingWarning && (
              <WarningMessage message={mergeMismatchListingWarning} />
            )}

            {mergeOfficeMismatchWarning && (
              <WarningMessage message={mergeOfficeMismatchWarning} />
            )}

            {mergeDuplicateListingWarning && (
              <WarningMessage message={mergeDuplicateListingWarning} />
            )}

            <FieldError>{formState.errors.listingIds?.message}</FieldError>
          </Stack>
        </MultiSelectionContextProvider>
      </CatalogMultiSelectionContextProvider>
      {footer}
      {ineligibilityCount > 0 ? (
        <Stack direction="column" gap="l">
          <div className={styles.subLabel}>
            <Content
              id={ContentId.IncompatibleForMergingWithSelectedListings}
            />
          </div>
          <div className={styles.tableContainer}>
            <ListingTable
              {...rest}
              event={event}
              entities={eligibleListings}
              entityCount={eligibleListings.length}
              selectedIds={listingIds}
              showUnselectableListingsOnly
              showMainColumnsOnly
              getListingNotEligibleForSelection={
                getListingNotEligibleForSelection
              }
            />
          </div>
        </Stack>
      ) : null}
      {sectionPromptAboveSuggestions && sectionPromptNode && (
        <Stack direction="column" gap="s">
          {sectionPromptNode}
        </Stack>
      )}
      {mergeSuggestionsListingIds.length > 0 && (
        <Stack direction="column" gap="l">
          <span className={shared.typography.title6}>
            <Content id={ContentId.Suggestions} />
          </span>
          {mergeSuggestionsListingIds.map((s, i) => (
            <ActiveSortTableColumnProvider
              key={`merge-suggestions-${i}`}
              entityType={ActionOutboxEntityType.Listing}
              defaultSorting={DefaultListingTableSorting}
            >
              <MergeListingsSuggestionsDisplay
                {...rest}
                event={event}
                listings={flattenedEligibleListings}
                listingIds={s}
                groupIdNameMap={groupIdNameMap}
                index={i}
                hasOfficeFeature={hasEnableOfficeFeature}
                exclusivitySourceClasses={
                  exclusivitySourceClassesBySuggestion[i]
                }
                disabled={
                  disabled ||
                  (hasGaMergedMaxDisplayFeature &&
                    pendingSectionListingIds != null &&
                    !sectionTypeSelected &&
                    isEqualIgnoringOrder(s, pendingSectionListingIds))
                }
                onMergeSuggestions={handleMergeSuggestions}
              />
              {!sectionPromptAboveSuggestions &&
                sectionPromptNode != null &&
                pendingSectionListingIds != null &&
                isEqualIgnoringOrder(s, pendingSectionListingIds) && (
                  <Stack direction="column" gap="s">
                    {sectionPromptNode}
                  </Stack>
                )}
            </ActiveSortTableColumnProvider>
          ))}
        </Stack>
      )}
      {!sectionPromptAboveSuggestions &&
        sectionPromptNode != null &&
        !mergeSuggestionsListingIds.some(
          (s) =>
            pendingSectionListingIds != null &&
            isEqualIgnoringOrder(s, pendingSectionListingIds)
        ) && (
          <Stack direction="column" gap="s">
            {sectionPromptNode}
          </Stack>
        )}
    </Stack>
  );
};

/**
 * POS-7295: exposes the table's `clearSelection` to `MergeListingsBody`, which renders above the
 * MultiSelectionContextProvider. Used to deselect the table when a suggestion merge is started.
 */
const ClearSelectionBridge = ({
  clearSelectionRef,
}: {
  clearSelectionRef: MutableRefObject<() => void>;
}) => {
  const { clearSelection } = useMultiSelectionContext();
  useEffect(() => {
    clearSelectionRef.current = clearSelection;
  }, [clearSelection, clearSelectionRef]);
  return null;
};

/**
 * Selectable listing table for the merge flow. Beyond rendering the table, it
 * keeps the form's `listingIds` in sync with the current row selection.
 *
 * It is rendered *inside* the MultiSelectionContextProvider so it can read the
 * authoritative selection straight from context. We deliberately do NOT use
 * `ListingTable`'s `onItemSelectionsChanged` callback: under
 * `Feature.TableRowSelectionV2` that callback emits only the newly-selected
 * delta, so replacing the form value with it drops previously-checked listings
 * and breaks the "select 2+ to merge" flow (POS-8673). Reading the full
 * selection from context is stable regardless of the flag and also reflects
 * de-selections, which the delta callback never reports.
 */
const MergeListingsSelectionTable = ({
  event,
  listingIds,
  eligibleListings,
  getListingNotEligibleForSelection,
}: {
  event: Event;
  listingIds: number[] | undefined;
  eligibleListings: (Listing | ListingGroup)[];
  getListingNotEligibleForSelection: (
    listings: Listing[] | null
  ) => { listing: Listing; ineligibleReason: string | undefined }[] | undefined;
}) => {
  const [flattenedEligibleListings, groupIdNameMap] =
    getFlattenedListings(eligibleListings);

  const { setValue, clearErrors } = useFormContext<MergeListingsInput>();

  const itemSelection = useMultiSelectionContextItemSelectionSelector();

  // Mirror this group's selection from MultiSelectionContext into the merge
  // form. `isGroupSelected` is the "select all" case (every eligible listing);
  // otherwise `items` holds the individually-checked listing ids.
  useEffect(() => {
    const selectedItem = itemSelection[event.virtId];
    if (selectedItem) {
      clearErrors('listingIds');
      const { isGroupSelected, items } = selectedItem;
      if (isGroupSelected) {
        setValue(
          'listingIds',
          eligibleListings.map((l) => l.id)
        );
      } else {
        const selectedListingIds = items
          .map((id) => Number(id))
          .filter((id: number) => !isNaN(id));
        setValue('listingIds', selectedListingIds);
      }
    }
  }, [clearErrors, eligibleListings, event.virtId, itemSelection, setValue]);

  return (
    <ListingTable
      event={event}
      entities={flattenedEligibleListings}
      entityCount={flattenedEligibleListings.length}
      selectedIds={listingIds}
      hideUnselectableListings
      showMainColumnsOnly
      // onItemSelectionsChanged intentionally omitted — see this component's
      // doc comment. Selection is synced to the form by the effect above
      // (reading MultiSelectionContext), not through this callback.
      getListingNotEligibleForSelection={getListingNotEligibleForSelection}
      listingGroupMap={groupIdNameMap}
    />
  );
};
